import React, { useCallback, useRef, useState } from 'react';
import { faBuildingCircleArrowRight, faCirclePlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'components/ui/button';
import { Form, Modal, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap-5';

import {
  bulkSyncToCrm,
  fetchRemainingCredits,
  offMarketPropertySearch,
  syncToCrm
} from '../../../helpers/prospect_api';
import { displayToast } from 'bundles/connect/helpers/display_toast';
import IncludeOwnerDataCheckbox from '../add_to_rethink/include_owner_data_checkbox';
import { useSelector } from 'react-redux';
import { selectCriteria } from '../../store/selectors/criteria';
import type { PropertyCrmSync } from 'bundles/connect/types';
import PropertyCrmSyncModalExporting from '../add_to_rethink/modal_exporting';
import PropertySyncBatchCompleteModal from '../add_to_rethink/property_sync_batch_complete_modal';

type AddPropertyToCrmProps = {
  asIcon?: boolean;
  bulk?: boolean;
  selectAll?: boolean;
  itemIds?: Set<string>;
  itemCount?: number;
  insightsPropertyId?: string;
  lastFetched?: Date;
  propertySyncedAt?: string;
}

export default function AddPropertyToCrm({
  asIcon = false,
  bulk = false,
  selectAll = false,
  itemIds = new Set<string>(),
  itemCount = 0,
  insightsPropertyId,
  lastFetched,
  propertySyncedAt
}: AddPropertyToCrmProps) {
  const [show, setShow] = useState(false);
  const [saveContact, setSaveContact] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncTime, setSyncTime] = useState(propertySyncedAt);
  const [remainingCredits, setRemainingCredits] = useState(0);
  const [propertyCrmSync, setPropertyCrmSync] = useState<PropertyCrmSync | undefined>(undefined);
  const [email, setEmail] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const criteria = useSelector(selectCriteria);

  const triggerRef = useRef(null);
  const cannotIncludeContacts = remainingCredits < itemCount && !lastFetched;

  const handleModalDismiss = () => {
    setShow(false);
    setSaveContact(false);
    setLoading(false);
    setSaving(false);
    setPropertyCrmSync(undefined);
    setSendEmail(true);
    setEmail('');
  };

  const assemblePropertyIds = async () => {
    let ids = [...itemIds];

    if (selectAll && criteria) {
      const results = await offMarketPropertySearch(criteria, 1000);
      ids = (results.hits || []).map(hit => hit.propertyId);
    }

    return ids;
  };

  const handleEmailCheck = () => {
    setSendEmail(!sendEmail);
    setEmail('');
  };

  const crmSync = (includeContacts?: boolean) => {
    syncToCrm({
      insights_property_id: String(insightsPropertyId),
      include_contacts: includeContacts
    }).then((res) => {
      if (res.id) {
        displayToast('Successfully Saved Property', 'success');
        setSyncTime(new Date().toLocaleString());
      } else {
        displayToast('Something went wrong', 'error');
      }
      handleModalDismiss();
    }).finally(() => {
      setSaving(false);
    });
  };

  const crmBulkSync = async (includeContacts: boolean, sendEmail: boolean, email: string) => {
    const ids = await assemblePropertyIds();

    bulkSyncToCrm({
      insights_property_ids: ids,
      include_contacts: includeContacts,
      send_finished_email: sendEmail,
      email: email.length > 0 ? email : null
    }).then((res) => {
      setPropertyCrmSync(res);
    }).catch(() => {
      displayToast('Something went wrong', 'error');
      handleModalDismiss();
    }).finally(() => {
      setSaving(false);
    });
  };

  const handleSubmit = useCallback(() => {
    setSaving(true);
    if (bulk) {
      crmBulkSync(saveContact, sendEmail, email);
    } else {
      crmSync(saveContact);
    }
  }, [insightsPropertyId, saveContact, sendEmail, email]);

  const fetchCredits = () => {
    setLoading(true);
    fetchRemainingCredits()
      .then((res) => {
        setRemainingCredits(res.remainingCredits);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (syncTime && !asIcon) {
    return <div className="text-muted small">Added at {syncTime}</div>;
  }

  const trigger = (
    <div className="align-content-center text-center h-100 p-2" ref={triggerRef} >
      <FontAwesomeIcon
        className="text-primary clickable squared"
        icon={faBuildingCircleArrowRight}
        onClick={() => setShow(true)}
      />
    </div>
  );

  const renderTooltip = (
    <Tooltip id={`add-to-list-tooltip-${insightsPropertyId}`}>
      Add Property
    </Tooltip>
  );

  return (
    <>
      <Modal show={show} onHide={handleModalDismiss} onShow={fetchCredits}>
        {!propertyCrmSync && (
          <>
            <Modal.Header className="flex-row" closeButton>
              <Modal.Title>
                Add {bulk && (selectAll || itemIds.size > 1) ? 'Properties' : 'Property'}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {loading ? (
                <div className="d-flex">
                  <Spinner animation="border" />
                </div>
              ) : (
                <>
                  <IncludeOwnerDataCheckbox
                    cannotIncludeContacts={cannotIncludeContacts}
                    checked={saveContact}
                    disabled={saving || cannotIncludeContacts}
                    onChange={() => setSaveContact(!saveContact)}
                  />
                  {bulk &&
                    (<div className="mx-2">
                      <Form.Check
                        checked={sendEmail}
                        id="send-email-rethink-list"
                        label="Email me when sync is complete"
                        type="checkbox"
                        onChange={handleEmailCheck}
                      />
                      {sendEmail && (
                        <div className="m-2">
                          <Form.Label className="mt-2">Email address</Form.Label>
                          <Form.Control
                            className="mt-2"
                            placeholder="Email address"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                          />
                        </div>
                      )}
                    </div>
                    )}
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button disabled={saving || loading} onClick={() => handleSubmit()}>
                Save {bulk && (selectAll || itemIds.size > 1) ? 'Properties' : 'Property'}
                {saving && (
                  <Spinner animation="border" size="sm" />
                )}
              </Button>
            </Modal.Footer>
          </>
        )}
        {(propertyCrmSync && propertyCrmSync.syncProgress < 100) && (
          <PropertyCrmSyncModalExporting
            propertyCrmSync={propertyCrmSync}
            willContact={propertyCrmSync.sendFinishedEmail}
            onHide={handleModalDismiss}
            onSyncComplete={() => setPropertyCrmSync({ ...propertyCrmSync, syncProgress: 100 })}
          />
        )}
        {(propertyCrmSync && propertyCrmSync.syncProgress === 100) && (
          <PropertySyncBatchCompleteModal
            saveContact={saveContact}
            onHide={handleModalDismiss}
          />
        )}
      </Modal>

      {asIcon ?
        (
          <OverlayTrigger
            overlay={renderTooltip}
            placement="right"
            popperConfig={{
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 8],
                  },
                }
              ]
            }}
          >
            {trigger}
          </OverlayTrigger>
        ) :
        (
          <Button icon={bulk ? faBuildingCircleArrowRight : faCirclePlus} variant="ghost" onClick={() => setShow(true)}>
            {bulk ? 'Add Properties' : 'Add as Property'}
          </Button>
        )}
    </>
  );
}
